/**
 * PowerMapView — mapa general de alimentación (PDU/SAI → equipos).
 *
 * Tres columnas:
 *  - Fuentes raíz (PDUs y SAIs que no cuelgan de otra fuente).
 *  - PDUs hijas (cascadas), si las hay.
 *  - Equipos consumidores con sus PSUs.
 *
 * Cada toma y cada PSU es clicable: se reutiliza la selección del canvas para
 * conectar desde una toma libre a una PSU libre. Los cables se dibujan en un
 * SVG superpuesto y se recalculan al hacer scroll o cambiar los filtros.
 */

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const CABLE_HEX = {
  negro: '#1f2937',
  gris: '#9ca3af',
  blanco: '#e5e7eb',
  rojo: '#ef4444',
  azul: '#3b82f6',
  verde: '#22c55e',
  amarillo: '#eab308',
  naranja: '#f97316',
  morado: '#a855f7',
  rosa: '#ec4899',
};

function colorHex(name) {
  return CABLE_HEX[String(name || '').toLowerCase()] || '#9ca3af';
}

function consumerKey(consumer) {
  return `${consumer.consumerType}:${consumer.id}`;
}

function outletKey(sourceKey, outletId) {
  return `${sourceKey}:${outletId}`;
}

// ── Tarjeta de fuente (PDU o SAI) ────────────────────────────────────────────

function SourceCard({
  source,
  selectedOutlet,
  onOutletSelect,
  headerRef,
  outletRefs,
}) {
  const isUps = source.kind === 'ups';
  const accent = isUps ? '#f59e0b' : 'var(--border)';
  const used = source.outlets_used || 0;
  const total = source.outlets_total || (source.outlets || []).length;
  const input = (source.inputConnections || [])[0] || null;

  return (
    <div
      className="power-map-source"
      style={{
        background: 'var(--bg-toolbar)',
        border: `2px solid ${isUps ? 'rgba(245,158,11,0.55)' : 'var(--border)'}`,
        borderRadius: 10,
        marginBottom: 12,
        overflow: 'hidden',
      }}
    >
      <div
        ref={el => { headerRef(el); }}
        style={{
          padding: '8px 10px',
          background: isUps ? 'rgba(245,158,11,0.10)' : 'var(--bg-card)',
          borderBottom: `1px solid ${isUps ? 'rgba(245,158,11,0.25)' : 'var(--border)'}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: '0.95rem' }}>{isUps ? '🔋' : '🔌'}</span>
          <span style={{
            fontWeight: 700,
            fontSize: '0.85rem',
            color: 'var(--text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
          }}>
            {source.name}
          </span>
          <span style={{
            fontSize: '0.62rem',
            fontWeight: 700,
            padding: '1px 6px',
            borderRadius: 4,
            background: isUps ? 'rgba(245,158,11,0.18)' : 'var(--bg-input)',
            color: isUps ? '#b45309' : 'var(--text-secondary)',
            letterSpacing: '0.04em',
          }}>
            {isUps ? 'SAI' : 'PDU'}
          </span>
        </div>
        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>
          {[source.rack_name, source.location_name].filter(Boolean).join(' · ') || 'Sin ubicación'}
          {total > 0 ? ` · ${used}/${total} tomas` : ''}
          {input ? ` · Entrada: ${input.pdu_name || 'PDU'} #${input.outlet_number ?? '?'}` : ''}
        </div>
      </div>

      {(source.outlets || []).length === 0 ? (
        <div style={{ padding: '8px 10px', fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
          Sin tomas configuradas.
        </div>
      ) : (
        (source.outlets || []).map(outlet => {
          const conn = outlet.connection;
          const isChildPdu = Boolean(outlet.child_pdu_id);
          const isSelected = selectedOutlet?.id === outlet.id;
          const cHex = isChildPdu ? '#f97316' : colorHex(conn?.cable_color);
          return (
            <div
              key={outlet.id}
              className="power-map-outlet"
              ref={el => { outletRefs.current[outletKey(source.key, outlet.id)] = el; }}
              role="button"
              tabIndex={0}
              onClick={() => onOutletSelect(outlet, source)}
              onKeyDown={e => e.key === 'Enter' && onOutletSelect(outlet, source)}
              title={
                conn
                  ? `${source.name} #${outlet.outlet_number} → ${conn.consumer_name || 'equipo'} (${conn.psu_label || 'PSU'})`
                  : isChildPdu
                  ? `${source.name} #${outlet.outlet_number} → ${outlet.child_pdu_name}`
                  : `Toma libre de ${source.name} — clic para conectar`
              }
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 8px',
                borderBottom: '1px solid rgba(255,255,255,0.05)',
                borderLeft: `3px solid ${isSelected ? '#f97316' : conn || isChildPdu ? cHex : 'transparent'}`,
                background: isSelected
                  ? 'rgba(249,115,22,0.14)'
                  : conn || isChildPdu
                  ? `${cHex}12`
                  : 'transparent',
                cursor: 'pointer',
                fontSize: '0.72rem',
                userSelect: 'none',
              }}
            >
              <span style={{ minWidth: 28, fontWeight: 700, color: 'var(--text-secondary)' }}>
                #{outlet.outlet_number}
              </span>
              <span style={{ minWidth: 40, color: 'var(--text-muted)' }}>
                {outlet.outlet_type || '—'}
              </span>
              {conn ? (
                <>
                  <span style={{ color: cHex, fontWeight: 700 }}>●</span>
                  <span style={{
                    flex: 1,
                    minWidth: 0,
                    color: '#16a34a',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {conn.consumer_name || conn.psu_label || 'Equipo'}
                  </span>
                </>
              ) : isChildPdu ? (
                <>
                  <span style={{ color: '#f97316', fontWeight: 700 }}>↳</span>
                  <span style={{
                    flex: 1,
                    minWidth: 0,
                    color: '#b45309',
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {outlet.child_pdu_name}
                  </span>
                </>
              ) : (
                <span style={{ flex: 1, color: 'var(--text-muted)', fontStyle: 'italic' }}>Libre</span>
              )}
              {(conn || isChildPdu) && <span style={{ fontSize: '0.68rem' }}>✂️</span>}
            </div>
          );
        })
      )}
      <div style={{ height: 3, background: isUps ? accent : 'transparent' }} />
    </div>
  );
}

// ── Tarjeta de consumidor ────────────────────────────────────────────────────

function ConsumerCard({
  consumer,
  psus,
  psuConnectionMap,
  selectedOutlet,
  onPsuSelect,
  headerRef,
  psuRefs,
}) {
  const key = consumerKey(consumer);
  return (
    <div
      className="power-map-consumer"
      style={{
        background: 'var(--bg-toolbar)',
        border: `2px solid ${selectedOutlet ? 'rgba(134,239,172,0.55)' : 'var(--border)'}`,
        borderRadius: 10,
        marginBottom: 12,
        overflow: 'hidden',
      }}
    >
      <div
        ref={el => { headerRef(el); }}
        style={{
          padding: '8px 10px',
          background: 'var(--bg-card)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: '0.9rem' }}>{consumer.deviceIcon}</span>
          <span style={{
            fontWeight: 700,
            fontSize: '0.85rem',
            color: 'var(--text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
          }}>
            {consumer.name}
          </span>
          <span style={{
            fontSize: '0.62rem',
            padding: '1px 6px',
            borderRadius: 4,
            background: 'var(--bg-input)',
            color: 'var(--text-secondary)',
          }}>
            {consumer.deviceLabel}
          </span>
        </div>
        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>
          {[consumer.subLabel, `${psus.length} PSU${psus.length === 1 ? '' : 's'}`].filter(Boolean).join(' · ')}
        </div>
      </div>

      {psus.length === 0 ? (
        <div style={{ padding: '8px 10px', fontSize: '0.73rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
          Sin PSUs registradas.
        </div>
      ) : (
        psus.map(psu => {
          const conn = psuConnectionMap[psu.id];
          const isFree = !conn;
          const highlight = Boolean(selectedOutlet && isFree);
          const cHex = conn ? colorHex(conn.cable_color) : 'transparent';
          return (
            <div
              key={psu.id}
              className="power-map-psu"
              ref={el => { psuRefs.current[`${key}:${psu.id}`] = el; }}
              role="button"
              tabIndex={0}
              onClick={() => onPsuSelect(psu, consumer.id, consumer.name, consumer.consumerType)}
              onKeyDown={e => e.key === 'Enter' && onPsuSelect(psu, consumer.id, consumer.name, consumer.consumerType)}
              title={
                conn
                  ? `Alimentada desde ${conn.pdu_name || 'fuente'} #${conn.outlet_number}`
                  : highlight
                  ? 'Clic para conectar la toma seleccionada aquí'
                  : 'PSU libre'
              }
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 8px',
                borderBottom: '1px solid rgba(255,255,255,0.05)',
                borderLeft: `3px solid ${conn ? cHex : highlight ? '#22c55e' : 'transparent'}`,
                background: conn ? `${cHex}12` : highlight ? 'rgba(34,197,94,0.10)' : 'transparent',
                cursor: 'pointer',
                fontSize: '0.72rem',
                userSelect: 'none',
              }}
            >
              <span style={{ minWidth: 28, color: 'var(--text-muted)' }}>⚡</span>
              <span style={{
                flex: 1,
                minWidth: 0,
                fontWeight: 600,
                color: 'var(--text-primary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {psu.label || psu.model || `PSU ${psu.id}`}
              </span>
              {conn ? (
                <>
                  <span style={{ color: cHex, fontWeight: 700 }}>●</span>
                  <span style={{
                    color: '#16a34a',
                    maxWidth: 120,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {conn.pdu_name} #{conn.outlet_number}
                  </span>
                  <span style={{ fontSize: '0.68rem' }}>✂️</span>
                </>
              ) : highlight ? (
                <span style={{ color: '#16a34a', fontWeight: 600, whiteSpace: 'nowrap' }}>← Conectar aquí</span>
              ) : (
                <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Sin alimentación</span>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

// ── Componente principal ─────────────────────────────────────────────────────

export default function PowerMapView({
  sources,
  consumers,
  consumerPsus,
  psuConnectionMap,
  selectedOutlet,
  onOutletSelect,
  onPsuSelect,
  onDisconnect,
  onDisconnectDaisy,
}) {
  const containerRef = useRef(null);
  const leftRef = useRef(null);
  const middleRef = useRef(null);
  const rightRef = useRef(null);
  const outletRefs = useRef({});
  const sourceHeaderRefs = useRef({});
  const consumerHeaderRefs = useRef({});
  const psuRefs = useRef({});
  const [cables, setCables] = useState([]);

  const rootSources = useMemo(
    () => sources.filter(s => !s.parent_pdu_id && !s.upstream_outlet_id),
    [sources]
  );
  const childSources = useMemo(
    () => sources.filter(s => s.parent_pdu_id || s.upstream_outlet_id),
    [sources]
  );
  const hasChildren = childSources.length > 0;

  const consumersByKey = useMemo(() => {
    const map = new Map();
    consumers.forEach(c => map.set(consumerKey(c), c));
    return map;
  }, [consumers]);

  const findConsumer = useCallback((conn) => {
    if (!conn) return null;
    const exactKey = `${conn.consumer_type}:${conn.consumer_id}`;
    if (consumersByKey.has(exactKey)) return consumersByKey.get(exactKey);
    // Las conexiones de SAI normalizan «server» a «rack_element» en backend.
    if (conn.consumer_type === 'server') {
      return consumersByKey.get(`server:${conn.consumer_id}`)
        || consumersByKey.get(`rack_element:${conn.consumer_id}`)
        || null;
    }
    if (conn.consumer_type === 'rack_element') {
      return consumersByKey.get(`rack_element:${conn.consumer_id}`)
        || consumersByKey.get(`server:${conn.consumer_id}`)
        || null;
    }
    return null;
  }, [consumersByKey]);

  // Lista estable de conexiones a dibujar
  const connections = useMemo(() => {
    const list = [];
    sources.forEach(source => {
      (source.outlets || []).forEach(outlet => {
        if (outlet.connection) {
          list.push({
            kind: 'psu',
            source,
            outlet,
            conn: outlet.connection,
          });
        }
        if (outlet.child_pdu_id) {
          list.push({
            kind: 'daisy',
            source,
            outlet,
          });
        }
      });
      // Entrada del SAI (si está alimentado desde una PDU/toma).
      (source.inputConnections || []).forEach(conn => {
        list.push({ kind: 'input', source, conn });
      });
    });
    return list;
  }, [sources]);

  const measure = useCallback(() => {
    if (!containerRef.current) return;
    const cRect = containerRef.current.getBoundingClientRect();
    const next = [];
    connections.forEach(item => {
      const lEl = item.kind === 'input'
        ? (() => {
            const upstream = sources.find(s => s.id === item.conn.pdu_id);
            const upstreamOutlet = upstream?.outlets?.find(o => o.id === item.conn.outlet_id);
            return upstream && upstreamOutlet
              ? outletRefs.current[outletKey(upstream.key, upstreamOutlet.id)]
              : null;
          })()
        : outletRefs.current[outletKey(item.source.key, item.outlet.id)];
      let rEl = null;
      if (item.kind === 'daisy') {
        const child = sources.find(s => s.id === item.outlet.child_pdu_id);
        rEl = child ? sourceHeaderRefs.current[child.key] : null;
      } else if (item.kind === 'input') {
        rEl = sourceHeaderRefs.current[item.source.key];
      } else {
        const consumer = findConsumer(item.conn);
        const psus = consumer ? (consumerPsus[consumer.id] || []) : [];
        const psu = item.conn.psu_component_id
          ? psus.find(p => p.id === item.conn.psu_component_id)
          : null;
        if (consumer) {
          rEl = psu
            ? psuRefs.current[`${consumerKey(consumer)}:${psu.id}`]
            : consumerHeaderRefs.current[consumerKey(consumer)];
        }
      }
      if (!lEl || !rEl) return;
      const lr = lEl.getBoundingClientRect();
      const rr = rEl.getBoundingClientRect();
      next.push({
        ...item,
        x1: lr.right - cRect.left,
        y1: lr.top - cRect.top + lr.height / 2,
        x2: rr.left - cRect.left,
        y2: rr.top - cRect.top + rr.height / 2,
      });
    });
    setCables(next);
  }, [connections, sources, consumerPsus, findConsumer]);

  useLayoutEffect(() => { measure(); }, [measure]);

  useEffect(() => {
    const columns = [leftRef.current, middleRef.current, rightRef.current].filter(Boolean);
    columns.forEach(el => el.addEventListener('scroll', measure, { passive: true }));
    window.addEventListener('resize', measure);
    return () => {
      columns.forEach(el => el.removeEventListener('scroll', measure));
      window.removeEventListener('resize', measure);
    };
  }, [measure, hasChildren]);

  // Recalcular también cuando cambia el tamaño del contenedor (sidebar, zoom...).
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  const handleCableClick = (item) => {
    if (item.kind === 'daisy') {
      onDisconnectDaisy?.({
        outletNumber: item.outlet.outlet_number,
        sourceName: item.source.name,
        sourceKind: item.source.kind,
        upsId: item.source.upsId,
        outletId: item.outlet.id,
        childPduId: item.outlet.child_pdu_id,
        childPduName: item.outlet.child_pdu_name,
      });
    } else {
      onDisconnect?.(item.conn);
    }
  };

  const columnStyle = {
    height: '100%',
    overflowY: 'auto',
    paddingRight: 6,
    zIndex: 2,
  };

  return (
    <div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        flexWrap: 'wrap',
        marginBottom: 10,
        fontSize: '0.74rem',
        color: 'var(--text-muted)',
      }}>
        <span><strong style={{ color: 'var(--text-secondary)' }}>Mapa general</strong> · toma → PSU</span>
        <span>⬜ libre · ● color = cable · ↳ = PDU hija</span>
        <span>🔋 SAI · 🔌 PDU</span>
        <span style={{ fontStyle: 'italic' }}>Clic en una toma y luego en una PSU libre para conectar.</span>
      </div>

      <div
        ref={containerRef}
        style={{
          position: 'relative',
          display: 'flex',
          gap: 0,
          height: 'calc(100vh - 330px)',
          minHeight: 520,
          overflow: 'hidden',
        }}
      >
        {/* Columnas */}
        <div ref={leftRef} style={{ ...columnStyle, width: hasChildren ? '30%' : '38%', flexShrink: 0 }}>
          <div style={{
            fontSize: '0.7rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'var(--text-muted)',
            marginBottom: 6,
          }}>
            Fuentes (PDU / SAI)
          </div>
          {rootSources.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', padding: '1rem 0' }}>
              No hay fuentes en la ubicación seleccionada.
            </div>
          ) : (
            rootSources.map(source => (
              <SourceCard
                key={source.key}
                source={source}
                selectedOutlet={selectedOutlet}
                onOutletSelect={onOutletSelect}
                headerRef={el => { sourceHeaderRefs.current[source.key] = el; }}
                outletRefs={outletRefs}
              />
            ))
          )}
        </div>

        {hasChildren && (
          <div ref={middleRef} style={{ ...columnStyle, width: '30%', flexShrink: 0, paddingLeft: 6 }}>
            <div style={{
              fontSize: '0.7rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: '#b45309',
              marginBottom: 6,
            }}>
              PDUs hijas (cascada)
            </div>
            {childSources.map(source => (
              <SourceCard
                key={source.key}
                source={source}
                selectedOutlet={selectedOutlet}
                onOutletSelect={onOutletSelect}
                headerRef={el => { sourceHeaderRefs.current[source.key] = el; }}
                outletRefs={outletRefs}
              />
            ))}
          </div>
        )}

        <div ref={rightRef} style={{ ...columnStyle, flex: 1, paddingLeft: 6 }}>
          <div style={{
            fontSize: '0.7rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'var(--text-muted)',
            marginBottom: 6,
          }}>
            Equipos alimentados
          </div>
          {consumers.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', padding: '1rem 0' }}>
              No hay equipos en la ubicación seleccionada.
            </div>
          ) : (
            consumers.map(consumer => (
              <ConsumerCard
                key={consumerKey(consumer)}
                consumer={consumer}
                psus={consumerPsus[consumer.id] || []}
                psuConnectionMap={psuConnectionMap}
                selectedOutlet={selectedOutlet}
                onPsuSelect={onPsuSelect}
                headerRef={el => { consumerHeaderRefs.current[consumerKey(consumer)] = el; }}
                psuRefs={psuRefs}
              />
            ))
          )}
        </div>

        {/* Cables */}
        <svg
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        >
          <defs>
            {Object.entries(CABLE_HEX).map(([name, hex]) => (
              <marker
                key={name}
                id={`pm-arrow-${name}`}
                markerWidth="7"
                markerHeight="7"
                refX="6"
                refY="3.5"
                orient="auto"
              >
                <polygon points="0 0, 7 3.5, 0 7" fill={hex} />
              </marker>
            ))}
            <marker id="pm-arrow-default" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
              <polygon points="0 0, 7 3.5, 0 7" fill="#9ca3af" />
            </marker>
            <marker id="pm-arrow-daisy" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
              <polygon points="0 0, 7 3.5, 0 7" fill="#f97316" />
            </marker>
          </defs>
          {cables.map(item => {
            const isDaisy = item.kind === 'daisy';
            const cHex = isDaisy ? '#f97316' : colorHex(item.conn?.cable_color);
            const colorKey = String(item.conn?.cable_color || '').toLowerCase();
            const marker = isDaisy
              ? 'pm-arrow-daisy'
              : CABLE_HEX[colorKey]
              ? `pm-arrow-${colorKey}`
              : 'pm-arrow-default';
            const midX = (item.x1 + item.x2) / 2;
            const d = `M ${item.x1} ${item.y1} C ${midX} ${item.y1}, ${midX} ${item.y2}, ${item.x2} ${item.y2}`;
            const title = isDaisy
              ? `${item.source.name} #${item.outlet.outlet_number} → ${item.outlet.child_pdu_name}`
              : item.kind === 'input'
              ? `${item.conn.pdu_name || 'PDU'} #${item.conn.outlet_number} → entrada de ${item.source.name}`
              : `${item.source.name} #${item.outlet.outlet_number} → ${item.conn.consumer_name || 'equipo'} · ${item.conn.psu_label || 'PSU'}`;
            return (
              <g key={`${item.source.key}-${item.kind}-${item.outlet?.id ?? item.conn?.id}`}>
                <path
                  d={d}
                  stroke="transparent"
                  strokeWidth={16}
                  fill="none"
                  style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                  onClick={() => handleCableClick(item)}
                >
                  <title>{title} — clic para desconectar</title>
                </path>
                <path
                  d={d}
                  className="power-map-cable"
                  stroke={cHex}
                  strokeWidth={2.5}
                  fill="none"
                  markerEnd={`url(#${marker})`}
                  strokeDasharray={item.conn?.cable_type === 'IEC C19/C20' ? '6 3' : undefined}
                  style={{ pointerEvents: 'none' }}
                />
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}
